import { RefObject, useEffect } from 'react';

/** How much of the remaining distance the pointer catches up each frame. */
const LERP = 0.12;
/** A total |dx| + |dy| below this many px counts as rest; the loop then stops. */
const REST = 0.1;
/** Where the spotlight sits before the first move: centre, 40% down. */
const START_Y = 0.4;

const motionAllowed = () => window.matchMedia('(prefers-reduced-motion: no-preference)').matches;
const finePointer = () => window.matchMedia('(hover: hover) and (pointer: fine)').matches;
const pageHidden = () => document.visibilityState === 'hidden';

/**
 * The landing scene's only requestAnimationFrame loop.
 *
 * It writes the pointer position onto the atmosphere node as four custom
 * properties (--auth-mx/-my in viewport px, --auth-px/-py unitless, centre 0)
 * and CSS turns them into tiny transform-only parallaxes. Nothing is read back
 * from layout: the spotlight is position: fixed, so client coordinates are
 * already page coordinates.
 *
 * Touch devices never install a loop (there is no cursor) and reduced motion
 * returns before installing anything, so --auth-px/-py keep their CSS
 * fallbacks and nothing responds. The loop stops itself at rest, on
 * pointerleave and while the tab is hidden. Cleanup is complete and idempotent
 * because StrictMode mounts, unmounts and remounts the effect in development.
 */
export function useLandingPointer(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const node = ref.current;
    if (!node || !motionAllowed() || !finePointer()) return undefined;

    let frame = 0;
    let running = false;
    let inside = false;
    let seeded = false;
    // The viewport centre at 40% height, so a spotlight that fades in before
    // the first move is centred rather than pinned to a corner.
    const current = { x: window.innerWidth / 2, y: window.innerHeight * START_Y };
    const target = { x: current.x, y: current.y };

    const write = () => {
      // Read once per frame, and only inside the frame: no layout thrash.
      const width = window.innerWidth;
      const height = window.innerHeight;
      node.style.setProperty('--auth-mx', `${current.x}px`);
      node.style.setProperty('--auth-my', `${current.y}px`);
      node.style.setProperty('--auth-px', `${(current.x / width) * 2 - 1}`);
      node.style.setProperty('--auth-py', `${(current.y / height) * 2 - 1}`);
    };

    const tick = () => {
      frame = 0;
      const dx = target.x - current.x;
      const dy = target.y - current.y;
      if (Math.abs(dx) + Math.abs(dy) < REST) {
        current.x = target.x;
        current.y = target.y;
        write();
        running = false;
        return;
      }
      current.x += dx * LERP;
      current.y += dy * LERP;
      write();
      frame = requestAnimationFrame(tick);
    };

    const start = () => {
      if (running || pageHidden()) return;
      running = true;
      frame = requestAnimationFrame(tick);
    };

    const onPointerMove = (event: PointerEvent) => {
      inside = true;
      target.x = event.clientX;
      target.y = event.clientY;
      if (!seeded) {
        // The first move of a session seeds the current point: no sweep in from
        // (0, 0) or from the viewport centre.
        seeded = true;
        current.x = target.x;
        current.y = target.y;
      }
      node.dataset.pointer = 'on';
      start();
    };

    const onPointerLeave = () => {
      inside = false;
      delete node.dataset.pointer;
    };

    const onVisibilityChange = () => {
      if (pageHidden()) {
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        running = false;
      } else if (inside) {
        // Only a pointer that is still over the page has a target worth
        // settling onto.
        start();
      }
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    // pointerenter/pointerleave do not bubble and the scene itself is
    // pointer-events: none, so "the pointer is over the page" is tracked on the
    // root element instead of on the atmosphere node.
    document.documentElement.addEventListener('pointerleave', onPointerLeave);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener('pointermove', onPointerMove);
      document.documentElement.removeEventListener('pointerleave', onPointerLeave);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [ref]);
}
